import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import ExpandedListTab from '../ExpandedListTab';
import { auctionLimitPropertiesType, auctionLimitType } from '../../types';
import ReadonlyField from '../fields/ReadonlyField';
import PrivilegedNumberField from '../fields/privileged/PrivilegedNumberField';
import Privileges from '../../utils/Privileges';
import useExtendedState from '../../hooks/useExtendedState';
import useSingleValidator from '../../hooks/useSingleValidator';
import { auctionLimitValueValidator } from '../../validators/auctionLimits';
import Constants from '../../utils/Constants';
import ApplyChangesButton from '../buttons/ApplyChangesButton';
import { Privileged } from '../../hocs/withPrivileges';
import useExtendedMutation from '../../hooks/useExtendedMutation';
import { ALL_AUCTION_LIMITS_QUERY_NAME, DELETE_AUCTION_LIMIT, UPDATE_AUCTION_LIMIT } from '../../graphql/auctionLimits';
import DeleteButton from '../buttons/DeleteButton';

AuctionLimitDetailTab.propTypes = {
    auctionLimit: auctionLimitType.isRequired,
    auctionLimitProps: auctionLimitPropertiesType.isRequired,
    onDetailDelete: PropTypes.func.isRequired,
};

function AuctionLimitDetailTab(props) {
    const {
        auctionLimit,
        auctionLimitProps,
        onDetailDelete,
    } = props;
    const { t } = useTranslation();
    const {
        minAuctionLimit,
        maxAuctionLimit,
    } = auctionLimitProps;
    const limitState = useExtendedState(auctionLimit.limit);
    const { validator, isValid } = useSingleValidator(true, useMemo(() => auctionLimitValueValidator(t, minAuctionLimit, maxAuctionLimit), [t, minAuctionLimit, maxAuctionLimit]));
    const [isDisabled, setIsDisabled] = useState(true);
    isValid()
        .then(() => setIsDisabled(false))
        .catch(() => setIsDisabled(true));
    const updateAuctionLimitMutation = useExtendedMutation(UPDATE_AUCTION_LIMIT, {
        variables: {
            id: auctionLimit.id,
            limit: limitState.value,
        },
        statesToResetOnSuccess: [limitState],
        successMessageOptions: { key: 'auction-limits.notification.auction-limit-update-success', dataProperty: 'updateAuctionLimit' },
        failureMessageOptions: {
            key: 'auction-limits.notification.auction-limit-update-failure',
            params: {
                memberName: auctionLimit.member.name,
                memberId: auctionLimit.member.memberId,
                productName: auctionLimit.product.name,
                productId: auctionLimit.product.productId,
            },
        },
        refetchQueries: [ALL_AUCTION_LIMITS_QUERY_NAME],
    });
    const deleteAuctionLimitMutation = useExtendedMutation(DELETE_AUCTION_LIMIT, {
        variables: { id: auctionLimit.id },
        successCallback: onDetailDelete,
        successMessageOptions: { key: 'auction-limits.notification.auction-limit-delete-success', dataProperty: 'deleteAuctionLimit' },
        failureMessageOptions: {
            key: 'auction-limits.notification.auction-limit-delete-failure',
            params: {
                memberName: auctionLimit.member.name,
                memberId: auctionLimit.member.memberId,
                productName: auctionLimit.product.name,
                productId: auctionLimit.product.productId,
            },
        },
        refetchQueries: [ALL_AUCTION_LIMITS_QUERY_NAME],
    });
    const sufficientPrivileges = [Privileges.AUCTION_LIMIT_EDIT];
    return (
        <ExpandedListTab
            className="auction-limit-detail-tab"
            title={t('auction-limits.auction-limit-detail')}
        >
            <div className="rich-list-row-expanded--fields">
                <ReadonlyField
                    label={t('auction-limits.member')}
                    value={`${auctionLimit.member.name} (${auctionLimit.member.memberId})`}
                    className="auction-limit-detail-tab--fields--member"
                />
                <ReadonlyField
                    label={t('auction-limits.product')}
                    value={`${auctionLimit.product.name} (${auctionLimit.product.productId})`}
                    className="auction-limit-detail-tab--fields--product"
                />
                <PrivilegedNumberField
                    sufficientPrivileges={sufficientPrivileges}
                    label={t('auction-limits.limit')}
                    state={limitState}
                    className="auction-limit-detail-tab--fields--limit"
                    inputId="auction-limit-detail-tab--fields--limit"
                    validator={validator}
                    step={Constants.AUCTION_LIMIT_STEP}
                    decrementDisabled={limitState.value - Constants.AUCTION_LIMIT_STEP < minAuctionLimit}
                    incrementDisabled={limitState.value + Constants.AUCTION_LIMIT_STEP > maxAuctionLimit}
                    min={minAuctionLimit}
                    max={maxAuctionLimit}
                    isRequired
                />
            </div>
            <Privileged sufficientPrivileges={sufficientPrivileges}>
                <div className="rich-list-row-expanded--buttons">
                    <div className="rich-list-row-expanded--buttons--left">
                        <ApplyChangesButton
                            extendedMutation={updateAuctionLimitMutation}
                            disabled={isDisabled}
                            confirmationMessage={t('auction-limits.confirm-update-auction-limit', {
                                memberName: auctionLimit.member.name,
                                memberId: auctionLimit.member.memberId,
                                productName: auctionLimit.product.name,
                                productId: auctionLimit.product.productId,
                            })}
                            extraConfirmationMessage={t('auction-limits.extra-confirm-update-auction-limit', {
                                memberName: auctionLimit.member.name,
                                memberId: auctionLimit.member.memberId,
                                productName: auctionLimit.product.name,
                                productId: auctionLimit.product.productId,
                            })}
                        />
                    </div>
                    <div className="rich-list-row-expanded--buttons--right">
                        <DeleteButton
                            extendedMutation={deleteAuctionLimitMutation}
                            confirmationMessage={t('auction-limits.confirm-delete-auction-limit', {
                                memberName: auctionLimit.member.name,
                                memberId: auctionLimit.member.memberId,
                                productName: auctionLimit.product.name,
                                productId: auctionLimit.product.productId,
                            })}
                        />
                    </div>
                </div>
            </Privileged>
        </ExpandedListTab>
    );
}

export default AuctionLimitDetailTab;
